import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Menu, Package } from 'lucide-react';
import { Drawer } from '../../components/ui/Drawer';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import type { ServicePackage, ProductTemplate } from '../../api/types';
import { EVENT_TYPES, marginPct } from './packages/constants';
import { PackageCard } from './packages/PackageCard';
import { inpSt, selSt } from './packages/sharedStyles';

// Re-export constants and shared styles for external consumers
export { SERVICE_TYPES, EVENT_TYPES, svcLabel, svcEmoji, marginPct, marginColor } from './packages/constants';
export { OptionsPanel } from './packages/OptionsPanel';
export { AttributeValuesEditor } from './packages/AttributeValuesEditor';

// ─── ServicePackagesSection ───────────────────────────────────────────────────

export function ServicePackagesSection() {
  const qc = useQueryClient();
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [filterEventType, setFilterEventType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', eventType: '', discountPercentage: '', discountLabel: '' });

  const { data: packages = [], isLoading } = useQuery<ServicePackage[]>({
    queryKey: ['service-packages'],
    queryFn: () => apiGet<ServicePackage[]>('/service-packages'),
  });

  const { data: templates = [] } = useQuery<ProductTemplate[]>({
    queryKey: ['product-templates'],
    queryFn: () => apiGet<ProductTemplate[]>('/product-templates'),
  });

  const createMutation = useMutation({
    mutationFn: () => apiPost('/service-packages', {
      name: createForm.name,
      description: createForm.description || undefined,
      eventType: createForm.eventType || null,
      discountPercentage: createForm.discountPercentage !== '' ? Number(createForm.discountPercentage) : null,
      discountLabel: createForm.discountLabel || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-packages'] });
      setCreateForm({ name: '', description: '', eventType: '', discountPercentage: '', discountLabel: '' });
      setShowCreate(false);
      toast.success('Pacchetto creato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Filtered packages
  const filteredPackages = useMemo(() => {
    let result = packages;
    if (filterEventType) {
      result = result.filter(p => p.eventType === filterEventType);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }
    return result;
  }, [packages, filterEventType, searchQuery]);

  // Compute package stats
  const packageStats = useMemo(() => {
    const stats: Record<string, { count: number; total: number }> = {};
    packages.forEach(pkg => {
      const total = pkg.items.reduce((s, i) => s + (i.priceOverride ?? i.basePrice), 0);
      stats[pkg.id] = { count: pkg.items.length, total };
    });
    return stats;
  }, [packages]);

  const selectedPackage = packages.find(p => p.id === selectedPackageId) ?? null;

  // Sidebar JSX variable (reused in desktop + mobile drawer)
  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
      {/* Header */}
      <div>
        <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.25rem' }}>Pacchetti Servizi</h3>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Seleziona un pacchetto</p>
      </div>

      {/* Package List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', flex: 1, overflowY: 'auto' }}>
        {filteredPackages.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', textAlign: 'center', padding: '1rem 0' }}>
            {packages.length === 0 ? 'Nessun pacchetto' : 'Nessun pacchetto corrisponde'}
          </p>
        ) : (
          filteredPackages.map(pkg => {
            const stats = packageStats[pkg.id];
            return (
              <button
                key={pkg.id}
                onClick={() => {
                  setSelectedPackageId(pkg.id);
                  setDrawerOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-card)',
                  background: selectedPackageId === pkg.id ? 'var(--color-primary)' : 'transparent',
                  color: selectedPackageId === pkg.id ? 'white' : 'var(--color-text)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  if (selectedPackageId !== pkg.id) {
                    e.currentTarget.style.backgroundColor = 'var(--glass-bg-card)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedPackageId !== pkg.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Package size={15} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: selectedPackageId === pkg.id ? 700 : 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pkg.name}</span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    background: selectedPackageId === pkg.id ? 'rgba(255,255,255,0.2)' : 'var(--glass-bg-card)',
                    padding: '0.125rem 0.375rem',
                    borderRadius: 'var(--radius-interactive)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stats?.count ?? 0} · €{(stats?.total ?? 0).toFixed(0)}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'rgba(255,255,255,0.10)' }} />

      {/* Filter Section */}
      <div>
        <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-faint)', marginBottom: '0.375rem' }}>
          Tipo evento
        </label>
        <select
          value={filterEventType}
          onChange={(e) => setFilterEventType(e.target.value)}
          style={{
            width: '100%',
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-sm)',
            padding: '0.5rem 0.625rem',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="">Tutti i tipi</option>
          {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Search */}
      <div>
        <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-faint)', marginBottom: '0.375rem' }}>
          Ricerca
        </label>
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Nome pacchetto…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.625rem 0.5rem 2rem',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* New Package Button */}
      <Button
        size="sm"
        icon={<Plus size={14} />}
        onClick={() => setShowCreate(true)}
        style={{ width: '100%' }}
      >
        Nuovo pacchetto
      </Button>
    </div>
  );

  return (
    <>
      <style>{`
        .service-packages-layout {
          background: var(--glass-bg-card);
          border: 1px solid oklch(from var(--color-text) l c h / 0.08);
          border-radius: var(--radius-card);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-height: auto;
        }

        .service-packages-main {
          display: flex;
          gap: 0;
          flex: 1;
          min-height: 400px;
          overflow: hidden;
        }

        .service-packages-sidebar {
          width: 220px;
          background: var(--glass-bg-card);
          border-right: 1px solid rgba(255,255,255,0.10);
          padding: 1rem;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          overflow-y: auto;
        }

        .service-packages-mobile-btn {
          display: none;
        }

        @media (max-width: 767px) {
          .service-packages-sidebar {
            display: none;
          }
          .service-packages-mobile-btn {
            display: flex;
          }
        }
      `}</style>

      <div className="service-packages-layout">
        {/* Header */}
        <div
          style={{
            padding: '0.875rem 1.25rem',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Gestisci i tuoi pacchetti servizi fotografici con sconto bundle
          </p>
          {/* Mobile hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="service-packages-mobile-btn"
            style={{
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              cursor: 'pointer',
              color: 'var(--color-text)',
              padding: '0.375rem 0.625rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Menu size={18} />
          </button>
        </div>

        {/* Main Content */}
        <div className="service-packages-main">
          {/* Sidebar */}
          <div className="service-packages-sidebar">
            {sidebarContent}
          </div>


          {/* Content Panel */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              padding: '1rem',
              overflow: 'hidden',
            }}
          >
            {isLoading ? (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento…</p>
            ) : selectedPackage ? (
              <PackageCard
                pkg={selectedPackage}
                templates={templates}
                isExpanded={true}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'var(--color-text-faint)',
                  fontSize: 'var(--text-sm)',
                  textAlign: 'center',
                }}
              >
                {packages.length === 0
                  ? 'Nessun pacchetto. Creane uno con il pulsante nella sidebar.'
                  : 'Seleziona un pacchetto dalla lista.'}
              </div>
            )}
          </div>
        </div>

        {/* Create Package Modal */}
        <Modal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          title="Nuovo pacchetto"
          width={560}
          footer={
            <>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Annulla</Button>
              <Button size="sm" onClick={() => createMutation.mutate()} loading={createMutation.isPending} disabled={!createForm.name.trim()}>Crea pacchetto</Button>
            </>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Nome *</label>
              <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} style={inpSt} placeholder="es. Pacchetto Silver Matrimonio" autoFocus />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Tipo evento</label>
              <select value={createForm.eventType} onChange={e => setCreateForm(f => ({ ...f, eventType: e.target.value }))} style={selSt}>
                <option value="">Tutti i tipi</option>
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Sconto bundle (%)</label>
              <input type="number" min="0" max="100" step="0.1"
                value={createForm.discountPercentage}
                onChange={e => setCreateForm(f => ({ ...f, discountPercentage: e.target.value }))}
                style={inpSt} placeholder="es. 10" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Etichetta sconto</label>
              <input value={createForm.discountLabel}
                onChange={e => setCreateForm(f => ({ ...f, discountLabel: e.target.value }))}
                style={inpSt} placeholder="es. Sconto pacchetto" />
            </div>
            <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Descrizione</label>
              <input value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} style={inpSt} placeholder="Opzionale" />
            </div>
          </div>
        </Modal>
      </div>
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Pacchetti servizio">
        {sidebarContent}
      </Drawer>
    </>
  );
}
